import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Flex,
  FlexCenter,
  FlexColumn,
  OverflowTooltip,
  StatusChip,
} from '@elementary/common-ui/components';
import dateRenderer from '@elementary/common-ui/components/cellRenderers/dateRenderer';
import userNameRenderer from '@elementary/common-ui/components/cellRenderers/userNameRenderer';
import { ChipStatus } from '@elementary/common-ui/components/Chip/StatusChip';
import Table, {
  Column,
  QuickFilter,
  TableFilters,
  TableEmptyState,
  ToggleColumnsMenu,
  ColumnType,
} from '@elementary/common-ui/components/Table';
import dateComparer from '@elementary/common-ui/utils/comparers/dateComparer';
import { useAuth } from '@frontegg/react';
import { CircularProgress, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useRecoilState } from 'recoil';
import * as atoms from 'shared/store/atoms';
import { PullRequest } from 'shared/types';
import { fontSize, radius, space } from '@elementary/common-ui/style/utils';

const PENDING_WAIT_TIME_MINUTES = 5;

const PendingAlert = styled(Flex)`
  background: ${({ theme }) => theme.palette.surface.tinted};
  border: 1px solid ${({ theme }) => theme.palette.border.selected};
  border-radius: ${radius('lg')};
  padding: ${space('lgPlus')};
  height: 52px;
  align-items: center;
  gap: ${space('lgPlus')};
`;

const Link = styled.a`
  font-size: ${fontSize('base')};
  color: ${({ theme }) => theme.palette.accent.primary};
`;

const TableHeader = styled(Flex)`
  gap: ${space('xxl')};
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
`;

type Props = {
  data: PullRequest[];
  loading: boolean;
  urlParamsPrefix: string;
  localStorageKey: string;
};

const statusLabelMap: Record<string, ChipStatus> = {
  canceled: 'close',
  not_found: 'error',
};

const statusDetailsMap: Record<string, string> = {
  pending: 'Pull request is being created.',
  open: 'Pull request opened successfully.',
  merged: 'Pull request was successfully merged.',
  canceled: 'Pull request was canceled.',
  not_found: 'The pull request could not be found.',
  error: 'An error occurred while creating the pull request.',
};

const PullRequestsTable = ({
  data,
  loading,
  urlParamsPrefix,
  localStorageKey,
}: Props) => {
  const [search, setSearch] = useState('');
  const [newPullRequests, setNewPullRequests] = useState<PullRequest[]>([]);
  const { user } = useAuth();
  const [latestPullRequestTimestamp, setLatestPullRequestTimestamp] =
    useRecoilState(atoms.latestPullRequestTimestamp);

  const columns: Column<PullRequest>[] = useMemo(
    () => [
      {
        id: 'title',
        header: 'Title',
        renderer: ({ title }) => (
          <OverflowTooltip>
            {title?.split(/:\s?/).pop()?.replace(/\.$/, '')}
          </OverflowTooltip>
        ),
      },
      {
        id: 'created_at',
        header: 'Created at',
        type: ColumnType.Date,
        renderer: (val) => dateRenderer(val.created_at),

        compareFunc: (a, b) => dateComparer(a.created_at!, b.created_at!),
        filterValueMatcher: ([from, to, emptyFlag], rowData) => {
          const time = rowData.created_at;

          if (emptyFlag === 'true') return !time;
          if (!time) return false;

          const timeDayjs = dayjs(time);

          return (
            (!from || dayjs(from).startOf('day').isBefore(timeDayjs)) &&
            (!to || dayjs(to).endOf('day').isAfter(timeDayjs))
          );
        },
      },
      {
        id: 'created_by',
        header: 'Created by',
        renderer: (val) => userNameRenderer([val.created_by]),
      },
      {
        id: 'status',
        header: 'Status',
        renderer: ({ status }) => (
          <StatusChip status={statusLabelMap[status] || status} />
        ),
      },
      {
        id: 'details',
        header: 'Details',
        showValueWhenEmpty: true,
        renderer: ({ status, error_message }) => (
          <OverflowTooltip>
            {status === 'error'
              ? error_message ?? statusDetailsMap.error
              : statusDetailsMap[status] ?? ''}
          </OverflowTooltip>
        ),
      },
      {
        id: 'pr_id',
        header: 'Pull request ID',
        renderer: (val) => (
          <Link href={val.url} target="_blank" rel="noreferrer">
            #{val.pr_id}
          </Link>
        ),
      },
      {
        id: 'pr_type',
        header: 'PR Type',
        hideColumn: true,
      },
    ],
    [],
  );

  const quickFilters: QuickFilter[] = useMemo(
    () => [
      {
        name: `Created by me (${
          data.filter((pr) => pr.created_by === user?.email).length
        })`,
        filters: [
          {
            property: 'created_by',
            operator: 'equals',
            value: [user!.email],
          },
        ],
      },
      {
        name: `Tests (${
          data.filter((pr) => pr.pr_type === 'test_management').length
        })`,
        filters: [
          {
            property: 'pr_type',
            operator: 'equals',
            value: ['test_management'],
          },
        ],
      },
      {
        name: `Assets (${
          data.filter((pr) => pr.pr_type === 'asset_management').length
        })`,
        filters: [
          {
            property: 'pr_type',
            operator: 'equals',
            value: ['asset_management'],
          },
        ],
      },
    ],
    [data, user],
  );

  const tableData = useMemo(
    () => data?.filter((pr) => pr.status !== 'pending'),
    [data],
  );

  const isPending = useMemo(
    () =>
      data?.find(
        (pr) =>
          pr.status === 'pending' &&
          dayjs(pr.created_at) >
            dayjs().subtract(PENDING_WAIT_TIME_MINUTES, 'minutes'),
      ),
    [data],
  );

  const checkForFirstSeenPrs = () => {
    if (tableData?.length > 0) {
      const sortedPrs = tableData.sort((pr1, pr2) =>
        dayjs(pr1.created_at).diff(pr2.created_at),
      );

      const latestPrTime = sortedPrs[sortedPrs.length - 1]?.created_at;

      if (!latestPullRequestTimestamp) {
        setLatestPullRequestTimestamp(latestPrTime);
        return;
      }

      const newPrs = tableData.filter(
        (pr) => dayjs(pr.created_at).diff(latestPullRequestTimestamp) > 0,
      );

      setNewPullRequests(newPrs);
      setLatestPullRequestTimestamp(latestPrTime);
    }

    setTimeout(() => {
      setNewPullRequests([]);
    }, 3000);
  };

  useEffect(() => {
    checkForFirstSeenPrs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  if (loading)
    return (
      <FlexCenter sx={{ width: '100%', height: '100%' }}>
        <CircularProgress />
      </FlexCenter>
    );

  return (
    <FlexColumn sx={{ gap: 2, overflow: 'hidden', height: '100%' }}>
      {isPending && (
        <PendingAlert>
          <CircularProgress size={14} sx={{ color: 'content.primary' }} />
          <Typography>
            Creating your pull request (this may take a few seconds)...
          </Typography>
        </PendingAlert>
      )}
      <TableHeader>
        <TableFilters
          data={tableData}
          columns={columns}
          urlParamsPrefix={urlParamsPrefix}
          search={search}
          onSearch={(val) => setSearch(val)}
          quickFilters={quickFilters}
        />
        <ToggleColumnsMenu
          columns={columns}
          localStorageKey={localStorageKey}
        />
      </TableHeader>
      <Table
        data={tableData}
        columns={columns}
        defaultSortKey="created_at"
        defaultSortDirection="desc"
        bottomScrollPadding={50}
        search={search}
        animateRows={newPullRequests.map((pr) => pr.id)}
        urlParamsPrefix={urlParamsPrefix}
        localStorageKey={localStorageKey}
        emptyStateRenderer={TableEmptyState}
      />
    </FlexColumn>
  );
};

export default PullRequestsTable;
